import { useState, useEffect, useRef, useMemo } from "react";
import { inventorySummaryApi, productApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination        from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Stock Entry?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed from inventory. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Inventory Modal ── */
function InventoryModal({ initial, products, inventory = [], onSave, onClose, saving }) {
  const isEdit = !!initial?.stockID;

  const [form, setForm] = useState({
    productID: initial?.productID ?? "",
    quantity:  initial?.quantity  ?? "",
  });
  const [errors, setErrors] = useState({});

  const selectedProduct = useMemo(() => {
    if (!form.productID) return null;
    return (products || []).find(p => Number(p.productID) === Number(form.productID));
  }, [form.productID, products]);

  const existingStock = useMemo(() => {
    if (!form.productID) return null;
    return (inventory || []).find(i => Number(i.productID) === Number(form.productID));
  }, [form.productID, inventory]);

  const validate = () => {
    const e = {};
    if (!form.productID) e.productID = "Product is required.";
    if (form.quantity === "" || form.quantity === null) e.quantity = "Quantity is required.";
    else if (isNaN(Number(form.quantity)) || Number(form.quantity) < 0) e.quantity = "Quantity must be a valid non-negative number.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, productID: Number(form.productID), quantity: Number(form.quantity) });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-inv-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><InvIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Stock Entry" : "New Stock Entry"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the stock details below." : "Fill in details to add inventory."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          {/* Side-by-side 2-column grid: Product (left, flex-1) + Quantity (right, 140px) */}
          <div className="rv-inv-modal-grid">
            {/* Product */}
            <div className="rv-field rv-inv-field-product">
              <label className="rv-label">Product <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={products}
                valueKey="productID"
                labelKey="productName"
                value={form.productID}
                onChange={val => {
                  setForm(f => ({ ...f, productID: val }));
                  setErrors(v => ({ ...v, productID: "" }));
                }}
                disabled={isEdit}
                placeholder="Select product…"
                searchPlaceholder="Search products…"
                error={errors.productID}
                pageSize={10}
              />
              {isEdit && <p className="rv-field-hint">Product cannot be changed after creation.</p>}
            </div>

            {/* Quantity */}
            <div className="rv-field rv-inv-field-qty">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input
                type="number"
                min="0"
                className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
                placeholder="e.g. 50"
                value={form.quantity}
                onChange={e => {
                  setForm(f => ({ ...f, quantity: e.target.value }));
                  setErrors(v => ({ ...v, quantity: "" }));
                }}
              />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>

          {/* Product Live Info Preview */}
          {selectedProduct && (
            <div className="rv-inv-preview-card">
              <div className="rv-inv-preview-info">
                <span className="rv-inv-preview-name">
                  {selectedProduct.productName}
                </span>
                <span className="rv-inv-preview-sub">
                  Code: <strong>{selectedProduct.productCode || "—"}</strong>
                  {selectedProduct.purityStandard ? ` • ${selectedProduct.purityStandard}` : ""}
                  {selectedProduct.pricingType ? ` • ${selectedProduct.pricingType}` : ""}
                </span>
              </div>
              <div className="rv-inv-preview-badge">
                <span className="rv-inv-preview-stock">
                  Current Stock: {existingStock ? existingStock.quantity : 0} pcs
                </span>
                {!isEdit && form.quantity !== "" && !isNaN(Number(form.quantity)) && Number(form.quantity) > 0 && (
                  <span style={{ fontSize: "10.5px", color: "var(--taupe-dark)" }}>
                    New total: {(existingStock ? Number(existingStock.quantity) : 0) + Number(form.quantity)} pcs
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Add Stock"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Operational Hub Nav ── */
function HubNav({ active = "Bulk Stock In" }) {
  const navTo = (target) => {
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: target }));
  };

  return (
    <div className="rv-hub-nav">
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stock Receipt" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stock Receipt")}
      >
        <ReceiptIcon /> Vendor Inward (Stock Receipts)
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stocks" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stocks")}
      >
        <TagIcon /> Jewelry Items & Barcodes
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Bulk Stock In" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Bulk Stock In")}
      >
        <BoxIcon /> Stock Balances
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Inventory Transaction" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Inventory Transaction")}
      >
        <TxnIcon /> Movement Ledger
      </button>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN INVENTORY SUMMARY PAGE
══════════════════════════════════════ */
export default function InventorySummaryPage() {
  const [inventory, setInventory]   = useState([]);
  const [products, setProducts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filterStock, setFilterStock] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns ── */
  const tableRef   = useRef(null);
  const [tableReady, setTableReady] = useState(false);

  // Columns: #, Product, Quantity, Stock Level, Last Updated, Actions
  useResizableColumns(tableRef, tableReady, [50, 220, 120, 140, 160, 100]);

  /* ── Fetch ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [invData, prodData] = await Promise.all([
        inventorySummaryApi.getAll(),
        productApi.getAll(),
      ]);
      setInventory(Array.isArray(invData) ? invData : invData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load inventory.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (!loading) setTableReady(true);
  }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Product name lookup ── */
  const productMap = Object.fromEntries(products.map(p => [p.productID, p.productName]));

/* ── Stats ── */
const totalSkus     = inventory.length;
const totalQty      = inventory.reduce((sum, i) => sum + (i.quantity ?? 0), 0);
const outOfStock    = inventory.filter(i => (i.quantity ?? 0) === 0).length;
const lowStock      = inventory.filter(i => (i.quantity ?? 0) > 0 && (i.quantity ?? 0) <= 10).length;

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.stockID) {
        await inventorySummaryApi.update(formData);
        showToast("Stock entry updated successfully.");
      } else {
        await inventorySummaryApi.create(formData);
        showToast("Stock entry added successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventorySummaryApi.remove(confirm.id);
      showToast("Stock entry deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Stock level helper ── */
  const getStockLevel = (qty) => {
    if (qty === 0)   return { label: "Out of Stock", cls: "rv-stock-pill--out" };
    if (qty <= 10)   return { label: "Low Stock",    cls: "rv-stock-pill--low" };
    if (qty <= 50)   return { label: "In Stock",     cls: "rv-stock-pill--in" };
    return              { label: "Well Stocked",  cls: "rv-stock-pill--well" };
  };

  /* ── Filtered list ── */
  const filtered = inventory.filter(item => {
    const pName = productMap[item.productID] ?? "";
    const matchSearch = pName.toLowerCase().includes(search.toLowerCase()) ||
      String(item.quantity).includes(search);

    const qty = item.quantity ?? 0;
    const matchStock =
      filterStock === "all"     ? true :
      filterStock === "out"     ? qty === 0 :
      filterStock === "low"     ? qty > 0 && qty <= 10 :
      filterStock === "instock" ? qty > 10 : true;

    return matchSearch && matchStock;
  });
 const {
  paged, page, totalPages, pageSize,
  setPage, setPageSize,
  pageStart, pageEnd, totalItems,
} = usePagination(filtered, 10);

  /* ── Format date ── */
  const fmtDate = (dt) => {
    if (!dt) return "—";
    const d = new Date(dt);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <div className="rv-inv-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <InventoryModal
          initial={modal.data}
          products={products}
          inventory={inventory}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      <HubNav active="Bulk Stock In" />

      {/* ── Topbar ── */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left">
          <h2 className="rv-inv-heading">Inventory Summary</h2>
          <span className="rv-inv-count">{inventory.length} entries</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> Add Stock
        </button>
      </div>
      {/* ── Filters ── */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by product name or quantity…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-filter-tabs">
          {[
            { key: "all",     label: "All" },
            { key: "instock", label: "In Stock" },
            { key: "low",     label: "Low Stock" },
            { key: "out",     label: "Out of Stock" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`rv-filter-tab ${filterStock === key ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStock(key); setPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table Card ── */}
      <div className="rv-card rv-inv-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-inv-empty">
              <div className="rv-loader" />
              <p>Loading inventory…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-inv-empty">
              <div className="rv-empty-icon"><InvIcon /></div>
              <p className="rv-empty-title">No inventory entries found</p>
              <p className="rv-empty-sub">
                {search ? "Try a different search term." : "Add your first stock entry to get started."}
              </p>
              {!search && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> Add Stock
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-inv-table-wrap">
              <table className="rv-table rv-inv-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Stock Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item, i) => {
                    const stock = getStockLevel(item.quantity ?? 0);
                    return (
                      <tr key={item.stockID} className="rv-inv-row">
                        <td className="rv-inv-idx">{pageStart + i}</td>
                        <td>
                          <div className="rv-inv-product-cell">
                            <span className="rv-inv-product-name">
                              {productMap[item.productID] ?? `Product #${item.productID}`}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="rv-inv-qty">{(item.quantity ?? 0).toLocaleString()}</span>
                        </td>
                        <td>
                          <span className={`rv-stock-pill ${stock.cls}`}>
                            <span className="rv-status-dot" />
                            {stock.label}
                          </span>
                        </td>
                        <td>
                          <div className="rv-actions">
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => setModal({ data: item })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({
                                id: item.stockID,
                                name: productMap[item.productID] ?? `Stock #${item.stockID}`,
                              })}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={paged}
            emptyIcon={<InvIcon />}
            emptyTitle="No inventory entries found"
            emptySubtitle={search ? "Try a different search term." : "Add your first stock entry to get started."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> Add Stock
                </button>
              )
            }
            renderCard={(item, i) => {
              const stock = getStockLevel(item.quantity ?? 0);
              return (
                <RvDataCard
                  key={item.stockID}
                  index={pageStart + i}
                  title={productMap[item.productID] ?? `Product #${item.productID}`}
                  subtitle={`Stock Entry #${item.stockID}`}
                  badge={
                    <span className={`rv-stock-pill ${stock.cls}`}>
                      <span className="rv-status-dot" />
                      {stock.label}
                    </span>
                  }
                  fields={[
                    { label: "Quantity", value: (item.quantity ?? 0).toLocaleString() },
                    { label: "Stock Level", value: stock.label },
                  ]}
                  onClick={() => setModal({ data: item })}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="rv-action-btn rv-action-btn--edit"
                        title="Edit"
                        onClick={() => setModal({ data: item })}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="rv-action-btn rv-action-btn--del"
                        title="Delete"
                        onClick={() => setConfirm({
                          id: item.stockID,
                          name: productMap[item.productID] ?? `Stock #${item.stockID}`,
                        })}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {/* Shared Pagination for Desktop & Mobile */}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalItems={totalItems}
          />
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view">
          <GripIcon /> Drag column borders to resize
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon(){ return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function InvIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 8h14M5 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4"/><path d="M9 3h6l2 5H7L9 3z"/></svg>; }
function BoxIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>; }
function WarnIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
function GripIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }

function ReceiptIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>; }
function TagIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function TxnIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M7 16V4m0 0L3 8m4-4l4 4m6 4v12m0 0l4-4m-4 4l-4-4"/></svg>; }
